/**
 * Battle Cards — types.
 *
 * A card is ONE COMBAT SPECIAL EFFECT wearing ONE GESTURE SUIT, named for the effect it
 * carries, in ONE OF TWO PRINTINGS. Sixteen effects × two tiers is thirty-two cards; a pet
 * equips ONE per suit, and that four-card loadout — four copies of each — is the sixteen-card
 * pile it draws from. There are no ranks, no poker hands and no percentage bonuses: the only
 * number that matters is how many copies of the same card you play.
 *
 * Pure types only — no logic, no RNG, no I/O.
 *
 * Design: requirements/BATTLE_CARDS_V4_LOADOUTS.md
 */

import type { CombatSpecialEffect, ElementId } from './petModels.js';
import type { CombatAction } from '../types/pvpTypes.js';

/**
 * A card's suit is a combat gesture, exactly as in the engine. Deliberately an alias of
 * `CombatAction` rather than a parallel union, so a suit can never drift from a gesture
 * the engine actually resolves.
 */
export type EffectCardGesture = CombatAction;

/**
 * **THE SUITLESS SLOT — what a Suikamon card's `gesture` says.**
 *
 * A Suikamon card is not a move. It is drawn from the same pile, it sits in the same hand and
 * it costs the same slot as a gesture card, but it plays into no meld and answers no legality
 * question: it is a copy of the Suikamon itself, and holding
 * `EVOLUTION_CARDS_PER_STEP` of them evolves it.
 *
 * A sentinel rather than a fifth `CombatAction`, because the combat wheel has exactly four
 * arms and every rule in `combatActionRules.ts` is written against them. And a sentinel that
 * **fails safe**: every suit path in the deck asks "is this card's gesture MY gesture", so a
 * card whose gesture matches none of the four falls out of every meld, every legality count
 * and every commit on its own — a missed filter loses the card from a stack, it can never
 * silently add power to one.
 */
export const SUIKAMON_CARD_SLOT = 'suikamon';

/**
 * The slot a card occupies in a hand: one of the four suits, or the suitless Suikamon card.
 *
 * `EffectCard.gesture` is typed as this rather than as {@link EffectCardGesture} so that every
 * place which indexes a per-suit record BY a card's gesture is a compile error until it says
 * what it does with a Suikamon card. That is the whole reason the union exists — the danger is
 * silent inclusion, and the compiler is the only thing that finds all of it.
 */
export type EffectCardSlot = EffectCardGesture | typeof SUIKAMON_CARD_SLOT;

/** Is this card slot one of the four combat suits, rather than the Suikamon card? */
export function isGestureSlot(slot: EffectCardSlot): slot is EffectCardGesture {
  return slot !== SUIKAMON_CARD_SLOT;
}

/**
 * Which printing of a card this is — and, since the collection ladder, WHICH RUNG THE PET
 * HAS COLLECTED IT TO.
 *
 * A card is no longer bought at a tier. Every card starts Common; a second copy makes it
 * Rare, a fourth makes it Epic (see `CARD_TIER_COPIES` / `getCardTierForCopies`). **There is
 * no Legendary**, and there deliberately cannot be: the top rung is a FULL STACK
 * (`EFFECT_CARD_COPIES`), the most copies a hand can ever play, so a fourth rung would be a
 * card you can own more of than you can use.
 *
 * **STILL NOT `RARITY_NAMES`.** That union is the five-value PET rarity (`Common` …
 * `Unique`). The two vocabularies now overlap in three words and nowhere else;
 * {@link CARD_TIER_LABEL} reuses the words, and a test pins that they are members of the pet
 * union so the two can never drift into saying different things with the same name.
 */
export type CardTier = 'common' | 'rare' | 'epic';

/**
 * Stable card id — `{gesture}-{effect}` for a Rare, `{gesture}-{effect}-common` for a Common.
 *
 * **RARES KEEP THE IDS THEY ALWAYS HAD, UNSUFFIXED**, which is the whole reason the scheme is
 * shaped this way: ids persist into `pvp_battles.battle_state` JSONB, so leaving the sixteen
 * existing ids untouched means no stored battle needs migrating at all.
 *
 * A string alias rather than a union of the thirty-two literals on purpose, for the same
 * reason: a stored battle must stay readable after a card is ever renamed. `getEffectCardById`
 * is the only thing that resolves one, and `LEGACY_CARD_ID_ALIASES` is where the next rename
 * goes.
 */
export type EffectCardId = string;

/**
 * One card in the deck. Effect plus suit plus printing — that is the whole identity.
 *
 * `value` and `chance` are exclusive: a card either has a magnitude that copies multiply,
 * or it is binary and copies buy turns. Never both — and neither ONLY on a base card, which
 * has no effect to size.
 */
export interface EffectCard {
  id: EffectCardId;
  /**
   * What the card DOES — and `none` for the four BASE cards, which is the whole point of them.
   *
   * Widened from `ActiveCombatSpecialEffect` when the base cards landed: a slot with no skill
   * collected plays a card that adds nothing at all, and "adds nothing" is exactly what
   * `COMBAT_SPECIAL_EFFECT.None` means. The engine needs no branch for it (`resolveMeld`'s
   * default already returns no outcome) and no base card is ever in `EFFECT_CARD_CATALOGUE`,
   * so nothing that lists, prices or sells skills can see one.
   */
  effect: CombatSpecialEffect;
  /**
   * The suit this card is played in — or {@link SUIKAMON_CARD_SLOT} for the Suikamon card,
   * which is played in none. See {@link EffectCardSlot}.
   */
  gesture: EffectCardSlot;
  /** Which printing. Same effect, same suit, same art — a different number. */
  tier: CardTier;
  /**
   * The element this card's damage or healing is coloured by, or `undefined` for a card that
   * carries none.
   *
   * DERIVED FROM THE ROSTER, exactly as `value` is: the modal element of the species that run
   * this effect in this slot. A pet whose own element matches gets a bounded bonus
   * ({@link applyMatchedElement}). **Binary cards never carry one** — matched element
   * multiplies a magnitude, and boosting a chance instead would break the certainty invariant
   * that four Rare copies is exactly 100%.
   */
  element?: ElementId;
  /** The effect, in player words. Never a flavour name — the name IS the rules. */
  name: string;
  /** The one line a player reads in under a second. */
  headline: string;
  /** Rules text, shown on the card face and in the catalogue. */
  description: string;
  art: string;
  /** Magnitude copies multiply. Mutually exclusive with {@link chance}. */
  value?: number;
  /** Base chance for a binary effect. Mutually exclusive with {@link value}. */
  chance?: number;
  /** Unit shown beside a value — "dmg", "shield", "HP". */
  unit?: string;
}

/** Where a card's base number came from, for the honesty note under the deck. */
export type CardValueSource = 'derived' | 'authored' | 'binary';

/**
 * What a card does at a given copy count.
 *
 * **`amount` is the number the player is SHOWN, not the raw magnitude behind it.** For most
 * cards those are the same thing; for a bleed they are not, and the shown one wins — a card
 * that prints a number the fight never uses is worse than a card with no number at all.
 */
export interface CardPreview {
  /**
   * `none` is the BASE card's: no effect, so no number, so nothing for a face to print. A kind
   * rather than an absent preview because every caller already switches on this, and the
   * alternative — returning `undefined` — makes all of them handle a null instead.
   */
  kind: 'value' | 'dot' | 'chance' | 'turns' | 'full-heal' | 'none';
  /**
   * The number to print: a magnitude, a per-turn tick, a percentage, or a turn count.
   *
   * **Zero on a `full-heal`**, which is the one kind with no number behind it: a full stack of a
   * healing card is a full recover commit, and that outcome is a DESTINATION (the whole bar) and
   * not an amount. Consumers that fold a card's number into a figure — the move button's heal —
   * already gate on `kind === 'value'` and so correctly add nothing.
   */
  amount: number;
  /** Ready-to-render text, e.g. `39 dmg`, `5/turn`, `100%`, `2 turns`, `FULL HP`. */
  label: string;
}

/** One rung of the copies ladder. N copies is ×N — no rung is a special case. */
export interface CopyLadderRow {
  copies: number;
  multiplier: number;
}

/** Why a proposed meld is not playable. */
export type MeldIllegalReason =
  | 'too-many-cards'
  | 'no-committed-gesture-card'
  | 'not-in-hand'
  | 'unknown-card';

/**
 * Result of checking a proposed meld against the core meld rules.
 *
 * An EMPTY meld is legal — committing a bare gesture with no cards is always allowed and
 * is how every cards-off battle plays. Only a non-empty meld has anything to break.
 */
export interface MeldLegality {
  legal: boolean;
  reason?: MeldIllegalReason;
}

/**
 * How big a meld plays: its copy count and what that multiplies to.
 *
 * **There is no rung that is not ×N**, on any card in the deck. A healing card used to carry
 * a `fullHeal` flag here — four copies filled the HP bar outright, whatever it had left —
 * which was the last surviving piece of the four-copy kill the deck removed for exactly the
 * same reason: a card that resolves a fight regardless of HP is not a bigger card, it is a
 * different game. Four Heals is four times face value now, and the battle carries on.
 */
export interface MeldStrength extends CopyLadderRow {
  /** The card being melded, or null when the meld is empty. */
  cardId: EffectCardId | null;
  /**
   * The printing that meld is made of, or null when the meld is empty.
   *
   * Carried on the strength rather than looked up again by the tie rule, which is the one
   * place a tier decides an OUTCOME: on equal copies, Rare takes the exchange. A second
   * catalogue lookup inside the engine would be the same fact stated twice.
   */
  tier: CardTier | null;
}

/** The two seats at a battle, named as the engine names them. */
export type CardBattleSide = 'challenger' | 'defender';

/** One player's card position. */
export interface CardSideState {
  hand: EffectCardId[];
  /** Discard-and-redraws already spent this round. */
  discardUsed: number;
  /**
   * **The round is in its PLAY step for this seat: the hand is final.**
   *
   * A round runs pitch → play. While this is false the seat is still shaping its hand and
   * may not commit a gesture; pitching, or accepting the deal as it stands, flips it true
   * and the hand can no longer change until the next round resets it.
   *
   * Optional because battles persisted before the two-step round exist in `battle_state`
   * JSONB with no such field; read it through the engine's `isHandLocked`, which treats a
   * missing flag as "still to confirm" rather than letting an old battle skip the step.
   */
  handLocked?: boolean;
  /**
   * **How many cards the top-of-round refill dealt** — they are the LAST this many in `hand`.
   *
   * Spending a meld closes the hand up and the refill appends, so everything before them is
   * a card the player KEPT from last round. The hand on screen holds each kept card in the
   * slot it already had and rings only these, which is what makes "keep it for later" a
   * choice a player can see they made rather than a hand that looks redealt every round.
   *
   * Zeroed by a pitch, whose draw lands in place and is shown by its own beat. Optional: a
   * battle stored before this existed reads as "nothing new", which marks nothing.
   */
  roundDrawn?: number;
  /**
   * **THE CARDS THIS SEAT'S CURRENT FIGHTER CANNOT PLAY — set aside, not destroyed.**
   *
   * The dead-card rule (`sweepDeadCards`) retires a suit the fighter in front can no longer
   * use: the recover cards once its recover budget is spent, the Suikamon cards once it stands
   * on the top rung. In 1v1 those facts only ever get truer, so the cards were simply dropped.
   *
   * **In a 3v3 they get FALSER, every time the trainer swaps.** One pile serves three pets, and
   * a dropped card was dropped for the whole team: a fresh Suikamon brought in with its own
   * recovers intact fought on a deck with no recover cards in it, and one that had never
   * evolved could not draw a Suikamon card because the pet before it had finished the ladder.
   * The deck has to be able to hand them back, so the sweep BENCHES rather than burns.
   *
   * Nothing here is ever drawn, counted or shown — a benched card is out of the deck in every
   * sense a player can see (`pileSize` counts the pile, not this). It only exists so
   * `beginCardRound` can put a card back the round its owner can use it again.
   *
   * Optional: every battle persisted before this existed comes back without it, and an empty
   * bench is exactly what an unswept deck has.
   */
  benched?: EffectCardId[];
}

/**
 * A pet's four equipped cards — one per suit, and a card may only sit in the slot matching
 * its own gesture.
 *
 * Keyed by {@link EffectCardGesture}, which is an alias of `CombatAction`, so the slot set
 * derives from `ALL_COMBAT_ACTIONS` and can never drift from the combat wheel.
 */
export type CardLoadout = Record<EffectCardGesture, EffectCardId>;

/**
 * One seat's private draw pile, as v2 persists it.
 *
 * Each side shuffles its OWN loadout — four cards, four copies each, sixteen in the pile —
 * so what the opponent equipped can never deny you a draw. The price is that contention is
 * gone (both sides hit their best stacks more often than they used to); it is symmetric, so
 * it moves no win-rate spread, and `card-suit-audit`'s HP-per-commit is the instrument that
 * sees it. Recorded as risk 1 in the requirement.
 */
export interface CardSidePile {
  /** Face-down draw pile, next card first. */
  pile: EffectCardId[];
  discard: EffectCardId[];
  /**
   * How many times this side's pile has been re-shuffled — by a recycle of its discard, or by
   * cards coming back off {@link CardSideState.benched} — and also its shuffle seed offset, which
   * is why it counts both rather than only the recycles.
   */
  reshuffles: number;
  /** The four cards this pile was built from, stored so a replay needs no pet lookup. */
  loadout: CardLoadout;
}

/**
 * The card position of a battle **as it was persisted before loadouts existed**: one pile,
 * shared by both seats.
 *
 * READ-ONLY AND NEVER CREATED AGAIN. It exists because `pvp_battles.battle_state` holds live
 * battles in this shape and they have to keep playing to their end — a stored blob that stops
 * loading is a battle a player cannot finish.
 */
export interface CardBattleStateV1 {
  /** Absent on the real stored blobs, which predate the discriminant. */
  version?: 1;
  seed: number;
  /** Face-down draw pile, next card first. */
  pile: EffectCardId[];
  discard: EffectCardId[];
  challenger: CardSideState;
  defender: CardSideState;
  /** How many times the discard has been recycled — also the reshuffle's seed offset. */
  reshuffles: number;
}

/**
 * The card position of a battle, as persisted inside `pvp_battles.battle_state`.
 *
 * Cards are stored as ids, never as objects — the catalogue is code, a battle stores
 * references into it. Each pile is shuffled once at creation and drawn off the top, and a
 * reshuffle re-shuffles that side's discard under `seed + reshuffles`, so nothing here has to
 * serialise RNG internals and a stored battle replays exactly.
 */
export interface CardBattleStateV2 {
  version: 2;
  seed: number;
  /**
   * **THE ROUND'S BOOSTED SUITS — the one thing both fighters can see and both can use.**
   *
   * `ROUND_BOOST_SUITS` of the four, rolled fresh at the top of every round: a move committed
   * in a boosted suit plays as if it held one more copy (`ROUND_BOOST_COPIES`). It is shared,
   * so it sits on the STATE rather than on a seat — and it costs neither player a card, which
   * is why nothing here draws, discards, or reshuffles.
   *
   * `boostRound` is the round counter the roll is seeded from, so a stored battle re-rolls the
   * same suits it was playing and a replay is exact.
   *
   * Both optional: a battle persisted before boosts existed has neither, and the engine reads
   * them through `roundBoosts`, which treats a missing row as a round with no boost at all.
   */
  boosts?: EffectCardGesture[];
  boostRound?: number;
  challenger: CardSideState & CardSidePile;
  defender: CardSideState & CardSidePile;
}

/**
 * The whole card position of a battle — either shape.
 *
 * **A UNION DISCRIMINATED ON `version`, so a v1 blob still loads.** Five functions carry the
 * branch (`buildPile`, `createCardBattleState`, `takeOne`, `discardPileFor`, `getCardHandView`)
 * and everything else reads through them; `cardEngine.test.ts` plays three rounds out of a
 * literal v1 fixture, because nothing else in the codebase proves a stored blob still works.
 */
export type CardBattleState = CardBattleStateV1 | CardBattleStateV2;

/**
 * What one player may be told about the cards.
 *
 * The opponent's HAND SIZE and discard count are public — that is the tell the design
 * calls for — but their cards never are. This is the only card shape that crosses the
 * API boundary toward a client.
 */
export interface CardHandView {
  hand: EffectCard[];
  /**
   * **The suits boosted this round** — a move committed in one of them plays a copy bigger.
   *
   * Public by definition, so unlike the hand it is the same list in both players' views. It is
   * SUITS, not cards: the boost puts nothing on the board and costs nobody a draw.
   */
  boosts: EffectCardGesture[];
  opponentHandSize: number;
  /**
   * **Suikamon cards each side is holding right now** — the nameplate's evolution cards.
   *
   * Public for BOTH seats, and only as counts: which cards an opponent holds is never sent,
   * but how close they are to evolving has to be visible or the evolution is an ambush. Same
   * kind of tell as {@link discardPileSize}, and the one the mechanic is played against.
   */
  suikamonHeld: number;
  opponentSuikamonHeld: number;
  discardUsed: number;
  discardsPerRound: number;
  pileSize: number;
  discardPileSize: number;
  /**
   * Which step of the round this player is in — false is PITCH, true is PLAY.
   *
   * The client renders the step off this rather than off a local flag of its own, for the
   * same reason it takes the hand wholesale: the server resolves the pitch, so the server
   * is the only thing that knows whether it has been resolved.
   */
  handLocked: boolean;
  /**
   * The cards this round's refill dealt — the last this many of {@link hand}; every card
   * before them was kept from last round. See `CardSideState.roundDrawn`.
   */
  roundDrawn?: number;
}
